import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './ResponsiveNavbar.css';

const ResponsiveNavbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    document.body.style.overflow = !isOpen ? 'hidden' : 'auto';
  };

  const toggleDropdown = (dropdown) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const navItems = [
    {
      title: 'Home',
      path: '/',
      icon: 'fas fa-home'
    },
    {
      title: 'About',
      path: '/about',
      icon: 'fas fa-info-circle',
      submenu: [
        { title: 'About Journal', path: '/about' },
        { title: 'Editorial Board', path: '/editorial-board' },
        { title: 'Editorial Team', path: '/editorial-team' }
      ]
    },
    {
      title: 'Publications',
      path: '/current-publications',
      icon: 'fas fa-book',
      submenu: [
        { title: 'Current Publications', path: '/current-publications' },
        { title: 'Archives', path: '/archives' },
        { title: 'Announcements', path: '/announcements' },
        { title: 'Search Articles', path: '/search' }
      ]
    },
    {
      title: 'Authors',
      path: '/authors',
      icon: 'fas fa-pen',
      submenu: [
        { title: 'Guide for Authors', path: '/guide-for-authors' },
        { title: 'Submit Paper', path: '/submit-paper' },
        // { title: 'Manuscript Template', path: '/manuscript-template' },
        { title: 'Review Process', path: '/review-process' },
        { title: 'Guidelines', path: '/guidelines' },
        { title: 'Ethics', path: '/ethics' }
      ]
    },
    {
      title: 'Policies',
      path: '/open-access-policy',
      icon: 'fas fa-shield-alt',
      submenu: [
        { title: 'Open Access Policy', path: '/open-access-policy' },
        { title: 'Copyright', path: '/copyright' },
        { title: 'Privacy Policy', path: '/privacy-policy' },
        { title: 'Publication Charges', path: '/publication-charges' },
        { title: 'Payment Methods', path: '/payment-methods' }
      ]
    },
    {
      title: 'Contact',
      path: '/contact',
      icon: 'fas fa-envelope'
    }
  ];

  return (
    <>
      <nav className={`responsive-navbar ${scrolled ? 'scrolled' : ''} ${isOpen ? 'menu-open' : ''}`}>
        <div className="navbar-container">
          {/* Logo Section */}
          <Link to="/" className="navbar-logo">
            <div className="logo-wrapper">
              <div className="logo-icon">
                <i className="fas fa-leaf"></i>
              </div>
              <div className="logo-text">
                <span className="logo-main">FUWJALS</span>
                <span className="logo-sub">Journal of Agriculture & Life Sciences</span>
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="navbar-desktop">
            <ul className="navbar-menu">
              {navItems.map((item, index) => (
                <li key={index} className={`navbar-item ${item.submenu ? 'navbar-dropdown' : ''}`}>
                  {item.submenu ? (
                    <>
                      <Link to={item.path} className="navbar-link">
                        <i className={`${item.icon} me-1`}></i>
                        {item.title}
                        <i className="fas fa-chevron-down ms-1"></i>
                      </Link>
                      <div className="dropdown-menu">
                        {item.submenu.map((subitem, subindex) => (
                          <Link
                            key={subindex}
                            to={subitem.path}
                            className="dropdown-item"
                          >
                            {subitem.title}
                          </Link>
                        ))}
                      </div>
                    </>
                  ) : (
                    <Link to={item.path} className="navbar-link">
                      <i className={`${item.icon} me-1`}></i>
                      {item.title}
                    </Link>
                  )}
                </li>
              ))}
            </ul>

            {/* Desktop Actions */}
            <div className="navbar-actions">
              <button className="navbar-search-btn">
                <i className="fas fa-search"></i>
              </button>
              <Link to="/login" className="navbar-login-btn">
                <i className="fas fa-user me-1"></i>
                Login
              </Link>
              <Link to="/submit-paper" className="navbar-submit-btn">
                <i className="fas fa-upload me-1"></i>
                Submit
              </Link>
            </div>
          </div>

          {/* Mobile Menu Toggle */}
          <button className="navbar-toggle" onClick={toggleMenu}>
            <span className={`toggle-line ${isOpen ? 'active' : ''}`}></span>
            <span className={`toggle-line ${isOpen ? 'active' : ''}`}></span>
            <span className={`toggle-line ${isOpen ? 'active' : ''}`}></span>
          </button>
        </div>

        {/* Mobile Navigation */}
        <div className={`navbar-mobile ${isOpen ? 'active' : ''}`}>
          <div className="mobile-menu-header">
            <div className="mobile-search">
              <input
                type="text"
                placeholder="Search articles..."
                className="mobile-search-input"
              />
              <button className="mobile-search-btn">
                <i className="fas fa-search"></i>
              </button>
            </div>
          </div>

          <ul className="mobile-menu">
            {navItems.map((item, index) => (
              <li key={index} className="mobile-menu-item">
                {item.submenu ? (
                  <>
                    <button
                      className="mobile-menu-link"
                      onClick={() => toggleDropdown(index)}
                    >
                      <span>
                        <i className={`${item.icon} me-2`}></i>
                        {item.title}
                      </span>
                      <i className={`fas fa-chevron-${activeDropdown === index ? 'up' : 'down'}`}></i>
                    </button>
                    <div className={`mobile-submenu ${activeDropdown === index ? 'active' : ''}`}>
                      {item.submenu.map((subitem, subindex) => (
                        <Link
                          key={subindex}
                          to={subitem.path}
                          className="mobile-submenu-link"
                        >
                          {subitem.title}
                        </Link>
                      ))}
                    </div>
                  </>
                ) : (
                  <Link to={item.path} className="mobile-menu-link">
                    <span>
                      <i className={`${item.icon} me-2`}></i>
                      {item.title}
                    </span>
                  </Link>
                )}
              </li>
            ))}
          </ul>

          <div className="mobile-menu-footer">
            <Link to="/login" className="mobile-action-btn">
              <i className="fas fa-user me-2"></i>
              Login / Register
            </Link>
            <Link to="/submit-paper" className="mobile-submit-btn">
              <i className="fas fa-upload me-2"></i>
              Submit Paper
            </Link>
          </div>
        </div>
      </nav>

      {/* Overlay */}
      {isOpen && (
        <div className="navbar-overlay" onClick={toggleMenu}></div>
      )}
    </>
  );
};

export default ResponsiveNavbar;